import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { computeBudgetStats, type StatableBudget } from "@/lib/budgetStats";
import { toast } from "sonner";
import { barterSplit } from "@/lib/barter";
import { calculatePackageTotals, type PricingMode } from "@/lib/budgetCalc";
import type { ClientSigner } from "@/components/admin/documents/types";

export interface ServiceType {
  id: string;
  name: string;
  slug: string;
  hours_min: number;
  hours_max: number;
  default_hourly_rate: number;
  created_at: string;
}

/** One line of a proposal (a service type or a custom item). */
export interface BudgetItem {
  id: string;
  budget_id: string;
  service_type_id: string | null;
  title: string;
  description: string | null;
  pricing_mode: PricingMode;
  hours: number | null;
  hourly_rate: number | null;
  fixed_price: number | null;
  monthly_amount: number | null;
  subtotal: number;
  sort_order: number;
  created_at: string;
}

export interface BudgetItemInput {
  service_type_id?: string | null;
  title: string;
  description?: string | null;
  pricing_mode: PricingMode;
  hours: number;
  hourly_rate: number;
  fixed_price: number;
  monthly_amount: number;
}

export type BudgetDocumentMode = "auto" | "richtext" | "pdf_upload";

export interface Budget {
  id: string;
  lead_id: string | null;
  client_project_id: string | null;
  service_type_id: string | null;
  title: string;
  description: string | null;
  pricing_mode: string;
  hours: number | null;
  hourly_rate: number | null;
  fixed_price: number | null;
  platform: string;
  platform_fee: number;
  discount_percent: number;
  gross_total: number;
  net_total: number;
  /** Σ recurring items — a separate, ongoing monthly charge, never part of gross/net_total. */
  recurring_monthly_total: number;
  /** Permuta: share (0–100) of net_total settled in services instead of cash. */
  barter_percent: number;
  /** Derived from barter_percent (buildBudgetRow). Cash to charge = net_total - barter_total. */
  barter_total: number;
  /** What the business receives in exchange (free text, shown to the client). */
  barter_description: string | null;
  /** Barter only: true = the monthly fee is still billed; false = it is part of the barter (default). */
  barter_charge_recurring: boolean;
  /** First time the budget became signed/accepted (DB trigger). Drives period stats. */
  accepted_at: string | null;
  status: string;
  signed_pdf_url?: string | null;
  finalized_at?: string | null;
  base_pdf_url?: string | null;
  base_document_hash?: string | null;
  /** How the signable document is produced: generated from items, authored, or uploaded. */
  document_mode: BudgetDocumentMode;
  richtext_content: string | null;
  source_pdf_url: string | null;
  pending_client_signers: ClientSigner[] | null;
  created_at: string;
  updated_at: string;
  // joined
  items: BudgetItem[];
  service_type?: ServiceType;
  lead?: { id: string; name: string; email: string } | null;
  client_project?: { id: string; title: string } | null;
}

/**
 * What the editor sends. Money/summary columns on `budgets` are derived
 * here from the items (see buildBudgetRow) — never trusted from the caller.
 */
export interface CreateBudgetData {
  title: string;
  description?: string;
  lead_id?: string;
  client_project_id?: string;
  platform: string;
  /** Percent (0–100) when platform === "other"; ignored otherwise. */
  custom_fee_percent?: number;
  discount_percent?: number;
  /** Permuta share (0–100); 0 = regular cash deal. */
  barter_percent?: number;
  barter_description?: string | null;
  barter_charge_recurring?: boolean;
  items: BudgetItemInput[];
  status?: string;
  /** Carried over by "Nova versão" so an authored document survives re-pricing. */
  document_mode?: BudgetDocumentMode;
  richtext_content?: string | null;
}

/** Derived `budgets` columns for a set of items — keeps legacy readers (triggers, reports) correct. */
export function buildBudgetRow(data: CreateBudgetData) {
  const totals = calculatePackageTotals({
    items: data.items.map((i) => ({
      pricingMode: i.pricing_mode,
      hours: i.hours,
      hourlyRate: i.hourly_rate,
      fixedPrice: i.fixed_price,
      monthlyAmount: i.monthly_amount,
    })),
    discountPercent: data.discount_percent ?? 0,
    platform: data.platform,
    customFee: data.custom_fee_percent ?? 0,
  });
  const barter = barterSplit(totals.net, data.barter_percent ?? 0);
  const first = data.items[0];
  const single = data.items.length === 1 ? first : null;
  return {
    row: {
      title: data.title,
      description: data.description ?? null,
      lead_id: data.lead_id ?? null,
      client_project_id: data.client_project_id ?? null,
      service_type_id: first?.service_type_id ?? null,
      pricing_mode: single ? single.pricing_mode : "package",
      hours: totals.totalHours || null,
      hourly_rate: single?.pricing_mode === "hourly" ? single.hourly_rate : null,
      fixed_price: single?.pricing_mode === "fixed" ? single.fixed_price : null,
      platform: data.platform,
      platform_fee: totals.feeRate,
      discount_percent: data.discount_percent ?? 0,
      gross_total: totals.gross,
      net_total: totals.net,
      recurring_monthly_total: totals.recurringMonthly,
      barter_percent: barter.barterPercent,
      barter_total: barter.barterTotal,
      barter_description: barter.barterPercent > 0 ? data.barter_description?.trim() || null : null,
      // Only meaningful inside a barter; reset otherwise so a later barter starts from the default.
      barter_charge_recurring: barter.barterPercent > 0 ? !!data.barter_charge_recurring : false,
      ...(data.status ? { status: data.status } : {}),
      ...(data.document_mode ? { document_mode: data.document_mode } : {}),
      ...(data.richtext_content !== undefined ? { richtext_content: data.richtext_content } : {}),
    },
    itemRows: data.items.map((i, idx) => ({
      service_type_id: i.service_type_id ?? null,
      title: i.title,
      description: i.description ?? null,
      pricing_mode: i.pricing_mode,
      hours: i.pricing_mode === "hourly" ? i.hours : null,
      hourly_rate: i.pricing_mode === "hourly" ? i.hourly_rate : null,
      fixed_price: i.pricing_mode === "fixed" ? i.fixed_price : null,
      monthly_amount: i.pricing_mode === "recurring" ? i.monthly_amount : null,
      subtotal: totals.itemSubtotals[idx],
      sort_order: idx,
    })),
    totals,
    barter,
  };
}

// Proposals feed the pipeline stage, the inbox, sales and finance stats —
// refreshing only ["budgets"] left those panels stale until a reload.
function invalidateBudgetViews(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ["budgets", "budget-stats", "finance-stats", "admin-inbox", "leads", "lead"]) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

async function replaceItems(
  budgetId: string,
  itemRows: ReturnType<typeof buildBudgetRow>["itemRows"],
) {
  // One transaction (migration 20260925130000): a failed insert no longer
  // leaves the proposal with its totals but zero items.
  const { error } = await supabase.rpc(
    "replace_budget_items" as never,
    {
      p_budget_id: budgetId,
      p_items: itemRows,
    } as never,
  );
  if (error) throw error;
}

/** Map a raw budget row (with `budget_items` joined) to the app's Budget shape. */
export function normalizeBudget<
  T extends {
    budget_items?: BudgetItem[] | null;
    service_types?: ServiceType | null;
    leads?: unknown;
    client_projects?: unknown;
  },
>(b: T) {
  const { budget_items, service_types, leads, client_projects, ...rest } = b;
  return {
    ...rest,
    items: [...(budget_items ?? [])].sort((a, c) => a.sort_order - c.sort_order),
    service_type: service_types || null,
    lead: (leads as Budget["lead"]) || null,
    client_project: (client_projects as Budget["client_project"]) || null,
  } as unknown as Budget;
}

// Service Types
export function useServiceTypes() {
  return useQuery({
    queryKey: ["service-types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("service_types" as never)
        .select("*")
        .order("name");
      if (error) throw error;
      return data as ServiceType[];
    },
  });
}

export function useCreateServiceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      name: string;
      slug: string;
      hours_min: number;
      hours_max: number;
      default_hourly_rate: number;
    }) => {
      const { data: result, error } = await supabase
        .from("service_types" as never)
        .insert(data as never)
        .select()
        .single();
      if (error) throw error;
      return result as ServiceType;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-types"] });
      toast.success("Tipo de serviço criado!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useDeleteServiceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("service_types" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["service-types"] });
      toast.success("Tipo de serviço removido!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

// Budgets
export function useBudgets(filters?: {
  status?: string;
  lead_id?: string;
  client_project_id?: string;
}) {
  return useQuery({
    queryKey: ["budgets", filters],
    queryFn: async () => {
      let query = supabase
        .from("budgets" as never)
        .select(
          "*, service_types(*), leads(id, name, email), client_projects(id, title), budget_items(*)",
        )
        .order("created_at", { ascending: false });

      if (filters?.status) query = query.eq("status", filters.status);
      if (filters?.lead_id) query = query.eq("lead_id", filters.lead_id);
      if (filters?.client_project_id)
        query = query.eq("client_project_id", filters.client_project_id);

      const { data, error } = await query;
      if (error) throw error;
      return (data as unknown as Parameters<typeof normalizeBudget>[0][]).map(normalizeBudget);
    },
  });
}

export function useBudgetsByProject(projectId: string | undefined) {
  return useQuery({
    queryKey: ["budgets", "project", projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const { data, error } = await supabase
        .from("budgets" as never)
        .select("*, budget_items(*)")
        .eq("client_project_id", projectId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as unknown as Parameters<typeof normalizeBudget>[0][]).map(normalizeBudget);
    },
    enabled: !!projectId,
  });
}

export function useCreateBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateBudgetData) => {
      const { row, itemRows } = buildBudgetRow(data);
      const { data: result, error } = await supabase
        .from("budgets" as never)
        .insert(row as never)
        .select()
        .single();
      if (error) throw error;
      const created = result as Budget;
      await replaceItems(created.id, itemRows);
      return created;
    },
    onSuccess: () => {
      invalidateBudgetViews(queryClient);
      toast.success("Orçamento salvo!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

export function useUpdateBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CreateBudgetData }) => {
      const { row, itemRows } = buildBudgetRow(data);
      const { data: result, error } = await supabase
        .from("budgets" as never)
        .update(row as never)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      await replaceItems(id, itemRows);
      return result as Budget;
    },
    onSuccess: () => {
      invalidateBudgetViews(queryClient);
      toast.success("Orçamento atualizado!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}

// Hard delete de um orçamento inteiro. O FK de budget_items/budget_signatures/
// payment_plans/payment_installments é ON DELETE CASCADE (some junto);
// contracts.budget_id é SET NULL (o contrato, se houver, fica sem o orçamento
// vinculado, mas não é apagado). payments.budget_id também é SET NULL — sem
// limpar aqui, a linha órfã ficaria contando pra sempre no useFinanceStats.
export function useDeleteBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // budget_subscriptions cascades on delete, but the Mercado Pago
      // preapproval would keep charging the client with no local row left.
      const { data: liveSubs, error: subsErr } = await supabase
        .from("budget_subscriptions" as never)
        .select("id")
        .eq("budget_id", id)
        .in("status", ["pending", "authorized", "paused"]);
      if (subsErr) throw subsErr;
      if ((liveSubs as unknown[] | null)?.length) {
        throw new Error("cancele a mensalidade no Mercado Pago (aba Pagamentos) antes de excluir");
      }
      const { error: paymentsErr } = await supabase
        .from("payments" as never)
        .delete()
        .eq("budget_id", id);
      if (paymentsErr) throw paymentsErr;
      const { error } = await supabase
        .from("budgets" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateBudgetViews(queryClient);
      toast.success("Orçamento excluído!");
    },
    onError: (error) => toast.error("Erro ao excluir orçamento: " + error.message),
  });
}

// Reprocessa a finalização quando todos assinaram mas signed_pdf_url ficou
// nulo (finalize-budget falhou silenciosamente na primeira tentativa).
// finalize-budget é idempotente: se já finalizado, retorna sem refazer.
export function useReprocessBudgetPdf() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (budgetId: string) => {
      const { data, error } = await supabase.functions.invoke("finalize-budget", {
        body: { budget_id: budgetId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateBudgetViews(queryClient);
      toast.success("PDF assinado reprocessado!");
    },
    onError: (error: Error) => toast.error("Erro ao reprocessar: " + error.message),
  });
}

export function useBudgetStats() {
  return useQuery({
    queryKey: ["budget-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("budgets" as never)
        .select("status, net_total, barter_total, created_at");
      if (error) throw error;

      return computeBudgetStats(data as StatableBudget[]);
    },
  });
}
